import {Form, Formik, FormikProps} from 'formik';
import {filter, find, isEmpty, isEqual, map} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi} from 'api';
import {ActionButton, Button, DataWrapper, PageHeader, PredefinedNotifierType, UnsavedChangesLeavePrompt} from 'components';
import {useApiData} from 'hooks';
import {Subgroup, Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {generateUtils} from 'utils';

import {GroupInfo} from './GroupInfo';
import {FieldError, GroupInfoService} from './GroupInfo/group-info.service';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.GROUPS;

const MIN_GROUP_COUNT_FOR_SHOWING_BOTTOM_BUTTONS = 10;

type FormFields = Group[];

type FormTemplateProps = {
    isSaveDisabled: (groups: Group[], initialGroups: Group[], dirty: boolean) => boolean;
} & FormikProps<FormFields>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    values, initialValues, dirty, isSaveDisabled, setValues
}: FormTemplateProps) => {
    const {t} = useTranslation();

    const renderActions = useCallback((onAdd, isDisabledSaveButton) => (
        <div className='tw-flex tw-items-center'>
            <ActionButton action='create' className='tw-mr-2' onClick={onAdd}>
                {t(T_KEYS.BUTTON.ADD_GROUP)}
            </ActionButton>
            <Button
                type='submit'
                color='primary'
                disabled={isDisabledSaveButton}
            >
                {t(T_KEYS.BUTTON.SAVE_CHANGES)}
            </Button>
        </div>
    ), [t]);

    const createGroup = useCallback<() => Group>(() => ({
        id: '',
        publicId: generateUtils.generateToken(),
        name: '',
        filePrefix: '',
        subgroups: []
    }), []);

    const onAddGroup = useCallback((groups: Group[]) => {
        setValues([...groups, createGroup()]);
    }, [createGroup, setValues]);

    const onRemove = useCallback((index: number) => {
        const updatedGroups = filter(values, (g, i) => i !== index);
        setValues(updatedGroups);
    }, [values, setValues]);

    return (
        <Form>
            {renderActions(() => onAddGroup(values), isSaveDisabled(values, initialValues, dirty))}
            <DataWrapper noData={!values?.length}>
                {map(values, ((group: Group, index: number) => {
                    const initialGroup = find(initialValues, {id: group.id});
                    return (
                        <GroupInfo
                            key={index}
                            group={group}
                            name={initialGroup?.name}
                            values={values}
                            index={index}
                            defaultExpanded={!initialGroup}
                            onRemove={onRemove}
                        />
                    );
                }))}
                {values.length >= MIN_GROUP_COUNT_FOR_SHOWING_BOTTOM_BUTTONS &&
                                    renderActions(() => onAddGroup(values), isSaveDisabled(values, initialValues, dirty))}
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const GroupsManagement: React.FC = () => {
    const [localGroups, setLocalGroups] = useState<Group[]>([]);
    const {t} = useTranslation();

    const updateGroups = useCallback((groups: Group[]) => {
        setLocalGroups(groups);
    }, []);

    const {send: requestGroups, loading: loadingGet} = useApiData(
        groupApi.getGroups, {onSuccess: updateGroups}
    );
    const {send: saveGroups, loading: loadingSave} = useApiData(
        groupApi.saveGroups, {onSuccess: updateGroups, notificationType: PredefinedNotifierType.Save}
    );

    const isLoading = useMemo(() => loadingGet || loadingSave, [loadingGet, loadingSave]);

    const getGroupErrors = useCallback((groups: Group[], group: Group): FieldError =>
        GroupInfoService.getGroupErrors(t, groups, group),
    [t]);

    const getSubgroupErrors = useCallback((subgroups: Subgroup[], subgroup: Subgroup): FieldError =>
        GroupInfoService.getSubgroupError(t, subgroups, subgroup),
    [t]);

    const getErrors = useCallback((groups: FormFields) =>
        groups.reduce((errors, cur, index, arr) => {
            const groupErrors = getGroupErrors(arr, cur);
            if (!isEmpty(groupErrors)) {
                errors[index] = groupErrors;
            }

            const subgroupErrors = cur?.subgroups.reduce((acc, subgroup, ind, subgroups) => {
                const subgroupsErrors = getSubgroupErrors(subgroups, subgroup);
                if (!isEmpty(subgroupsErrors)) {
                    acc[ind] = subgroupsErrors;
                }
                return acc;
            }, []);

            if (!isEmpty(subgroupErrors)) {
                if (isEmpty(errors[index])) {
                    errors[index] = {};
                }
                errors[index].subgroups = subgroupErrors;
            }

            return errors;
        }, []),
    [getGroupErrors, getSubgroupErrors]);

    const getGroupsDTO = useCallback((groups: Group[]) => map(groups,
        ({id, filePrefix, publicId, name, subgroups}) =>
            ({id, filePrefix, publicId, name, subgroups})), []);

    const isSaveDisabled = useCallback((groups: Group[], initialGroups: Group[], dirty: boolean) =>
        !(dirty && !isEqual(getGroupsDTO(groups), getGroupsDTO(initialGroups))) ||
        !groups?.every((group: Group) => {
            const isValidSubgroups = group.subgroups?.every((subgroup: Subgroup) =>
                isEmpty(getSubgroupErrors(group.subgroups, subgroup)));
            return isEmpty(getGroupErrors(groups, group)) && isValidSubgroups;
        }),
    [getGroupsDTO, getGroupErrors, getSubgroupErrors]);

    const onSubmit = useCallback((groups: FormFields) => {
        saveGroups(getGroupsDTO(groups));
    }, [saveGroups, getGroupsDTO]);

    useEffect(() => {
        requestGroups();
    }, [requestGroups]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <DataWrapper isLoading={isLoading}>
                <Formik
                    enableReinitialize
                    initialValues={localGroups ?? []}
                    validate={getErrors}
                    onSubmit={onSubmit}
                >
                    {(props) => (
                        <FormTemplate
                            {...props}
                            isSaveDisabled={isSaveDisabled}
                        />
                    )}
                </Formik>
            </DataWrapper>
        </Fragment>
    );
};
